import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeature, useFeatureLabel } from '../../lib/tenant.js';
import { useT } from '../../i18n/index.js';
import { TripSummary, rebookSearch } from './TripCard.js';
import type { BookingView } from '../Ticket.js';

/**
 * Journeys already made, collapsed.
 *
 * Closed by default because history is not what anyone opens this screen for — the trip
 * they are about to take is, and a year of past journeys pushing it off the fold makes the
 * app worse the more you use it.
 *
 * The rows are read-only. A past trip carries no ticket worth showing and nothing worth
 * emailing, so the only thing offered is making the journey again — and only when the
 * operator has that switched on.
 */
export function PastTrips({
  bookings,
  timezone,
}: {
  bookings: BookingView[];
  timezone: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const rebook = useFeature('rebook');
  const rebookLabel = useFeatureLabel('rebook', t('m.bookAgain'));

  if (bookings.length === 0) return null;

  return (
    <section className="mt-8">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls="past-trips"
        className="flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-left"
        style={{ borderColor: 'var(--color-line)' }}
      >
        <Chevron open={open} />
        <span className="flex-1 text-sm font-semibold">{t('m.pastTrips')}</span>
        <span
          className="tabular rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ background: 'var(--color-primary-wash)', color: 'var(--color-primary)' }}
        >
          {bookings.length}
        </span>
      </button>

      {open && (
        <div id="past-trips">
          <p className="mt-3 text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('m.pastTripsHint')}
          </p>
          <ul className="mt-2 flex flex-col gap-2" style={{ opacity: 0.75 }}>
            {bookings.map((booking) => (
              <li
                key={booking.reference}
                className="rounded-xl border"
                style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
              >
                {/* A div, not a Link: there is nothing behind a past trip to navigate to. */}
                <div className="flex items-center gap-3 p-3">
                  <TripSummary booking={booking} timezone={timezone} />
                </div>
                {rebook && (
                  <div
                    className="flex flex-wrap gap-2 border-t px-3 py-2"
                    style={{ borderColor: 'var(--color-line)' }}
                  >
                    <Link className="trip-action" to={rebookSearch(booking)}>
                      {rebookLabel}
                    </Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{
        transform: open ? 'rotate(90deg)' : 'none',
        transition: 'transform 120ms ease',
        color: 'var(--color-muted)',
      }}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
